import { useInfiniteQuery, useQuery } from 'react-query'

import { mapDTOtoVideo } from '../models/Video'
import { listVideos, videoLoader } from '../api/videosApi'
import queryClient from '../../queryClient'

const VIDEO_QUERY_KEY = 'video'

const videoQueryKeys = {
  get: (videoId: number) => [VIDEO_QUERY_KEY, videoId],
  list: (...params: unknown[]) => [VIDEO_QUERY_KEY, 'list', ...params],
}

const videoQuery = (videoId: number) => {
  return {
    queryKey: videoQueryKeys.get(videoId),
    queryFn: async () => {
      const video = mapDTOtoVideo(await videoLoader.load(videoId))

      // an id of -1 means the video doesn't exist
      if (video.id === -1) {
        // we're keeping the id of the video, however, so we can perform operations with it
        // e.g. removing deleted videos from spots
        video.id = videoId
      }

      return video
    },
    enabled:
      videoId !== undefined &&
      queryClient.isFetching(videoQueryKeys.list()) === 0,
    keepPreviousData: true,
    staleTime: 10 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  }
}

const useVideoQuery = (videoId: number) => useQuery(videoQuery(videoId))

const getVideo = (videoId: number) =>
  queryClient.fetchQuery(videoQuery(videoId))

export interface VideoSearchParams {
  search: string
  category: number
  includeUnpublished: boolean
}

const useVideoSearchInfiniteQuery = (
  searchParams: Partial<VideoSearchParams> = {}
) => {
  const { search, includeUnpublished, category } = searchParams

  return useInfiniteQuery({
    queryKey: videoQueryKeys.list(searchParams),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await listVideos({
        search: search || undefined,
        include_unpublished_p: includeUnpublished,
        p: pageParam,
        album_id: category,
        size: 60,
      })

      const videos = response.data.map(mapDTOtoVideo)
      videos.forEach((video) => {
        queryClient.cancelQueries(videoQueryKeys.get(video.id))
        queryClient.setQueryDefaults(videoQueryKeys.get(video.id), {
          cacheTime: 10 * 60 * 1000, // 10 mins
          staleTime: 10 * 60 * 1000, // 10 mins
        })
        queryClient.setQueryData(videoQueryKeys.get(video.id), () => video, {})
      })

      const videoIds = videos.map((v) => v.id)

      return {
        videoIds,
        total: response.total_count,
        page: response.p,
        nextPage:
          response.size + response.offset < response.total_count
            ? +response.p + 1
            : undefined,
        previousPage:
          response.p && +response.p > 1 ? +response.p - 1 : undefined,
      }
    },
    cacheTime: 5 * 60 * 1000, // 5 mins; only cache when not searching
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    getPreviousPageParam: (firstPage) => firstPage.previousPage,
  })
}

export { useVideoQuery, useVideoSearchInfiniteQuery, getVideo }
